import React, { useState, useEffect } from 'react';
import { X, Check, Sparkles, Crown, Loader2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

/**
 * UpgradeModal - Shows when user tries to access a locked premium feature
 * Displays Smart Plaite and Genius Plaite subscription options
 * Integrates with Stripe for payment
 */
const UpgradeModal = ({
  show,
  upgradeContext,
  onClose,
  userId,
  currentTier = 'free'
}) => {
  const [plans, setPlans] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [isCheckingOut, setIsCheckingOut] = useState(false);
  const [error, setError] = useState(null);

  // Fetch available plans
  useEffect(() => {
    if (show) {
      fetchPlans();
    }
  }, [show]);

  const fetchPlans = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await fetch(`${BACKEND_URL}/api/billing/plans`);
      if (!response.ok) throw new Error('Failed to load plans');
      const data = await response.json();
      setPlans(data.plans || []);
    } catch (err) {
      console.error('[UpgradeModal] Error fetching plans:', err);
      setError('Unable to load subscription plans');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSelectPlan = async (planKey) => {
    if (!userId || isCheckingOut) return;
    
    setSelectedPlan(planKey);
    setIsCheckingOut(true);
    setError(null);

    try {
      console.log('[UpgradeModal] Creating checkout session for:', planKey);
      
      const response = await fetch(`${BACKEND_URL}/api/billing/create-checkout-session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: userId,
          price_key: planKey,
          origin_url: window.location.origin
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to create checkout session');
      }

      const data = await response.json();
      console.log('[UpgradeModal] Checkout session created:', data);
      
      // Redirect to Stripe Checkout
      if (data.checkout_url || data.session_url) {
        const checkoutUrl = data.checkout_url || data.session_url;
        console.log('[UpgradeModal] Redirecting to Stripe:', checkoutUrl);
        window.location.href = checkoutUrl;
      } else {
        throw new Error('No checkout URL returned');
      }
    } catch (err) {
      console.error('[UpgradeModal] Checkout error:', err);
      setError(err.message || 'Unable to start checkout. Please try again.');
      setIsCheckingOut(false);
      setSelectedPlan(null);
    }
  };

  if (!show) return null;

  // Get context message based on upgrade trigger
  const getContextMessage = () => {
    const messages = {
      'ai_miq': 'Unlock real-time AI guidance that adapts to you',
      'nutrition_goals': 'Set smart daily targets to stay on track',
      'meal_planner': 'Get personalized meal plans that fit your lifestyle',
      'smart_picks': 'Discover healthy swaps for your cravings',
      'smart_swaps': 'Discover healthy swaps for your cravings',
      'recipe_swap': 'Get AI-powered healthy alternatives to your favorites',
      'cookbook': 'Save recipes and log meals with one tap',
      'smart_hub': 'Your AI coach saves recipes and guides your choices',
      'weekly_brief': 'Get weekly insights on your progress',
      'micronutrients': 'Track what matters to optimize your nutrition',
      'barcode': 'Scan and log packaged foods instantly',
      'restaurant_search': 'Make smarter choices when eating out',
      'metabolic_profile': 'Understand how your body responds to food',
      'body_composition': 'Get personalized recommendations for your body type',
      'trend_charts': 'See your progress over time',
      'edit_portion': 'Adjust portions for accurate tracking',
      'smart': 'Start planning your nutrition with smart daily targets',
      'genius': 'Unlock MIQ Smart Coach — your AI nutrition coach',
      'health_intelligence': 'Unlock MIQ Smart Coach & Health Intelligence'
    };
    return messages[upgradeContext] || 'Upgrade to unlock real-time AI guidance';
  };

  // Filter plans based on context and current tier
  const getRelevantPlans = () => {
    // Show all non-annual plans, with annual as an option
    return plans.filter(plan => {
      // If user has enhanced tier, only show genius plans
      if (currentTier === 'enhanced' || currentTier === 'smart_plaite') {
        return plan.tier === 'genius';
      }
      // Show monthly plans primarily
      return plan.interval === 'month' || plan.is_best_value;
    });
  };

  const relevantPlans = getRelevantPlans();

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0,0,0,0.9)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 10000,
      padding: '16px'
    }}>
      <div style={{
        background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)',
        borderRadius: '24px',
        maxWidth: '420px',
        width: '100%',
        maxHeight: '90vh',
        overflow: 'auto',
        border: '1px solid rgba(255,255,255,0.1)'
      }}>
        {/* Header */}
        <div style={{ 
          padding: '20px 20px 0 20px',
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'flex-start'
        }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
              <Sparkles size={20} style={{ color: '#667eea' }} />
              <h2 style={{ margin: 0, fontSize: '20px', fontWeight: '700', color: 'white' }}>
                Upgrade Your Intelligence
              </h2>
            </div>
            <p style={{ margin: 0, fontSize: '13px', color: 'rgba(255,255,255,0.6)' }}>
              {getContextMessage()}
            </p>
          </div>
          <button 
            onClick={onClose}
            style={{ 
              background: 'rgba(255,255,255,0.1)', 
              border: 'none', 
              borderRadius: '50%',
              width: '32px',
              height: '32px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer', 
              color: 'rgba(255,255,255,0.7)'
            }}
          >
            <X size={18} />
          </button>
        </div>

        {/* Content */}
        <div style={{ padding: '20px' }}>
          {isLoading ? (
            <div style={{ textAlign: 'center', padding: '40px 0' }}>
              <Loader2 
                size={32} 
                style={{ color: '#667eea', animation: 'spin 1s linear infinite' }} 
              />
              <p style={{ marginTop: '12px', color: 'rgba(255,255,255,0.6)' }}>
                Loading plans...
              </p>
            </div>
          ) : error && plans.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px 0', color: '#f44336' }}>
              <p>{error}</p>
              <button
                onClick={fetchPlans}
                style={{
                  marginTop: '12px',
                  padding: '10px 20px',
                  background: 'rgba(244, 67, 54, 0.1)',
                  border: '1px solid #f44336',
                  borderRadius: '8px',
                  color: '#f44336',
                  cursor: 'pointer'
                }}
              >
                Try Again
              </button>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {/* Smart Plaite Plan */}
              {relevantPlans.find(p => p.key === 'smart_plaite_monthly') && (
                <PlanCard
                  plan={relevantPlans.find(p => p.key === 'smart_plaite_monthly')}
                  isSelected={selectedPlan === 'smart_plaite_monthly'}
                  isCheckingOut={isCheckingOut && selectedPlan === 'smart_plaite_monthly'}
                  onSelect={() => handleSelectPlan('smart_plaite_monthly')}
                  variant="smart"
                />
              )}
              
              {/* Genius Plaite Monthly - Highlighted */}
              {relevantPlans.find(p => p.key === 'genius_plaite_monthly') && (
                <PlanCard
                  plan={relevantPlans.find(p => p.key === 'genius_plaite_monthly')}
                  isSelected={selectedPlan === 'genius_plaite_monthly'}
                  isCheckingOut={isCheckingOut && selectedPlan === 'genius_plaite_monthly'}
                  onSelect={() => handleSelectPlan('genius_plaite_monthly')}
                  variant="genius"
                  isPopular={true}
                />
              )}
              
              {/* Genius Plaite Annual - Best Value */}
              {relevantPlans.find(p => p.key === 'genius_plaite_annual') && (
                <PlanCard
                  plan={relevantPlans.find(p => p.key === 'genius_plaite_annual')}
                  isSelected={selectedPlan === 'genius_plaite_annual'}
                  isCheckingOut={isCheckingOut && selectedPlan === 'genius_plaite_annual'}
                  onSelect={() => handleSelectPlan('genius_plaite_annual')}
                  variant="genius"
                  isBestValue={true}
                />
              )}
            </div>
          )}

          {/* Error message */}
          {error && plans.length > 0 && (
            <p style={{ 
              marginTop: '12px', 
              fontSize: '12px', 
              color: '#f44336',
              textAlign: 'center'
            }}>
              {error}
            </p>
          )}

          {/* Footer */}
          <div style={{ marginTop: '20px', textAlign: 'center' }}>
            <p style={{ fontSize: '11px', color: 'rgba(255,255,255,0.4)', margin: '0 0 8px 0' }}>
              Cancel anytime • Secure payment via Stripe
            </p>
            <p style={{ fontSize: '10px', color: 'rgba(255,255,255,0.3)', margin: 0 }}>
              ✨ Starts helping immediately. Gets smarter over time.
            </p>
          </div>
        </div>
      </div>

      {/* Spin animation */}
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};


// ═══════════════════════════════════════════════════════════════════════════════
// PLAN CARD COMPONENT
// ═══════════════════════════════════════════════════════════════════════════════
const PlanCard = ({ plan, isSelected, isCheckingOut, onSelect, variant, isPopular, isBestValue }) => {
  const isGenius = variant === 'genius';
  
  return (
    <div style={{
      border: isGenius 
        ? '2px solid #667eea'
        : '1px solid rgba(255,255,255,0.15)',
      borderRadius: '16px',
      padding: '18px',
      background: isGenius 
        ? 'linear-gradient(135deg, rgba(102, 126, 234, 0.15) 0%, rgba(118, 75, 162, 0.15) 100%)'
        : 'rgba(255,255,255,0.03)',
      position: 'relative',
      transition: 'all 0.2s ease'
    }}>
      {/* Badge */}
      {(isPopular || isBestValue) && (
        <div style={{
          position: 'absolute',
          top: '-10px',
          right: '16px',
          padding: '4px 12px',
          background: isBestValue 
            ? 'linear-gradient(135deg, #4CAF50 0%, #8BC34A 100%)'
            : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          borderRadius: '12px',
          fontSize: '10px',
          fontWeight: '700',
          color: 'white',
          textTransform: 'uppercase',
          letterSpacing: '0.5px'
        }}>
          {isBestValue ? '💰 Best Value' : '⭐ Most Popular'}
        </div>
      )}
      
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <div style={{
            width: '36px',
            height: '36px',
            borderRadius: '10px',
            background: isGenius 
              ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
              : 'rgba(76, 175, 80, 0.2)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            {isGenius ? <Crown size={18} style={{ color: 'white' }} /> : <Check size={18} style={{ color: '#4CAF50' }} />}
          </div>
          <div>
            <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '700', color: 'white', letterSpacing: '0.2px' }}>
              {plan.tier_name || plan.name}
            </h3>
            {plan.subtitle && (
              <p style={{ margin: '3px 0 0', fontSize: '11px', color: isGenius ? '#667eea' : '#4CAF50', fontWeight: '500' }}>
                {plan.subtitle}
              </p>
            )}
            <p style={{ margin: '2px 0 0', fontSize: '10px', color: 'rgba(255,255,255,0.4)' }}>
              {plan.interval === 'year' ? 'Annual' : 'Monthly'} plan
            </p>
          </div>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={{ fontSize: '22px', fontWeight: '700', color: isGenius ? '#667eea' : '#4CAF50' }}>
            ${plan.price}
          </div>
          <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.5)' }}>
            /{plan.interval === 'year' ? 'year' : 'mo'}
          </div>
        </div>
      </div>
      
      {/* Description */}
      {plan.description && (
        <p style={{ 
          margin: '0 0 4px 0', 
          fontSize: '12px', 
          color: 'rgba(255,255,255,0.7)',
          lineHeight: '1.4'
        }}>
          {plan.description}
        </p>
      )}
      
      {/* Bridging Line (Genius only) */}
      {plan.bridging_line && (
        <p style={{ 
          margin: '0 0 8px 0', 
          fontSize: '12px', 
          color: 'rgba(255,255,255,0.9)',
          fontWeight: '500',
          lineHeight: '1.4'
        }}>
          {plan.bridging_line}
        </p>
      )}
      
      {/* Emotional Hook (Genius only) */}
      {plan.hook && (
        <p style={{ 
          margin: '0 0 12px 0', 
          fontSize: '11px', 
          color: '#667eea',
          fontStyle: 'italic',
          fontWeight: '500'
        }}>
          {plan.hook}
        </p>
      )}
      
      {/* Features */}
      <ul style={{ 
        margin: '0 0 16px 0', 
        padding: '0', 
        listStyle: 'none',
        fontSize: '12px'
      }}>
        {plan.features.slice(0, isGenius ? 6 : 4).map((feature, idx) => (
          <li key={idx} style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '8px',
            marginBottom: '6px',
            color: 'rgba(255,255,255,0.8)'
          }}>
            <Check size={14} style={{ color: isGenius ? '#667eea' : '#4CAF50', flexShrink: 0 }} />
            {feature}
          </li>
        ))}
        {plan.features.length > (isGenius ? 6 : 4) && (
          <li style={{ 
            fontSize: '11px', 
            color: 'rgba(255,255,255,0.5)',
            marginLeft: '22px'
          }}>
            + {plan.features.length - (isGenius ? 6 : 4)} more features
          </li>
        )}
      </ul>
      
      {/* Trial notice */}
      {plan.trial_days > 0 && (
        <div style={{
          padding: '8px 12px',
          background: 'rgba(102, 126, 234, 0.1)',
          borderRadius: '8px',
          marginBottom: '12px',
          fontSize: '11px',
          color: '#667eea',
          textAlign: 'center'
        }}>
          ✨ Starts helping immediately. Gets smarter over time.
        </div>
      )}
      
      {/* CTA Button */}
      <button
        onClick={onSelect}
        disabled={isCheckingOut}
        style={{
          width: '100%',
          padding: '14px 16px',
          background: isGenius 
            ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
            : 'rgba(76, 175, 80, 0.2)',
          border: isGenius ? 'none' : '1px solid rgba(76, 175, 80, 0.5)',
          borderRadius: '12px',
          color: 'white',
          fontSize: '14px',
          fontWeight: '600',
          cursor: isCheckingOut ? 'wait' : 'pointer',
          opacity: isCheckingOut ? 0.7 : 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '8px'
        }}
      >
        {isCheckingOut ? (
          <>
            <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} />
            Processing...
          </>
        ) : isGenius ? (
          <>🧠 Get Genius Plaite</>
        ) : (
          <>🎯 Build My Plan</>
        )}
      </button>
    </div>
  );
};

export default UpgradeModal;
